import { useEffect, useState } from "react"
import { assetRegisters } from "routes/asset-register/controller/asset-registers"
import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from "@mui/material"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { Register } from "./register"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { emptyArray } from "lib/empty-array"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { useReferenceState } from "lib/@hooks/use-reference"
import { AssetRegistersTop, RegisterPaginationBottom, RegisterPaginationTop } from "slot-definitions"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { Repeat } from "lib/@components/repeat"
import { pick } from "lib/pick"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { useScrollRestoration } from "lib/use-scroll-restoration"
import { FieldHeader } from "./plugins/regime-display/field-header"
import { Bound } from "lib/@components/binding/Bound"
import { NoResults } from "lib/@components/no-results/no-results"
import { AssetPagination } from "./asset-pagination"

const sortableRegisterField = {
    register: "name",
    regime: "regime",
    assets: "assets",
    status: "status",
    lastModified: "modified",
}

export function RegisterList() {
    const [skip, setSkip] = useState(0)
    const [take, setTake] = useReferenceState("register-page-size", 10)
    const [search] = useSearchParam("search")
    const [sortArr] = useReferenceState("register-list-sort", ["register"], "register-sort")
    const statusColumnWidth = 180
    const rawSort = sortArr?.[0] || ""
    const descending = rawSort.startsWith("-")
    const sortField = descending ? rawSort.slice(1) : rawSort
    const registerSort = sortableRegisterField[sortField]
    const sortMode = registerSort ? { sort: registerSort, descending } : {}

    const { items: registers, totalCount } = assetRegisters.useResults({
        skip,
        take,
        search,
        ...sortMode,
    }) ?? { items: emptyArray, totalCount: 0 }

    const theme = useTheme()
    const scroller = useScrollRestoration()
    const registerHeaders = [
        { field: "register" },
        { field: "regime" },
        { field: "assets" },
        { field: "status" },
        { field: "lastModified" },
    ]
    const hoverBg = theme.palette.action.hover

    useEffect(resetPageOnSortChange, [rawSort])

    RegisterPaginationTop.usePlug(<AssetPagination dataCy="register-pagination-top" />, [
        take,
        setTake,
        skip,
        setSkip,
        totalCount,
    ])

    RegisterPaginationBottom.usePlug(<AssetPagination dataCy="register-pagination" />, [
        take,
        setTake,
        skip,
        setSkip,
        totalCount,
    ])

    return (
        <Bound displayFields={registerHeaders} sorter="register-list-sort">
            <If truthy={registers === emptyArray}>
                <LoadingScreen description="Retrieving Asset Registers" />
                <Else>
                    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                        <AssetRegistersTop.Slot />
                        <Box sx={{ flexShrink: 0 }}>
                            <RegisterPaginationTop.Slot
                                take={take}
                                setTake={setTake}
                                skip={skip}
                                setSkip={setSkip}
                                totalCount={totalCount}
                            />
                        </Box>
                        <If truthy={registers.length === 0}>
                            <NoResults
                                children="No Asset Registers"
                                childrenStyle={{ fontWeight: 700, fontSize: "1.25rem" }}
                            />
                            <Else>
                                <FitInRemainingSpace
                                    ref={scroller}
                                    Component={TableContainer}
                                    leaveSpace={56}
                                    sx={{
                                        minHeight: theme.spacing(16),
                                        "& .MuiTableCell-root": {
                                            whiteSpace: "nowrap",
                                            py: 2,
                                        },
                                        "& .MuiTableBody-root .MuiTableCell-root": {
                                            borderRadius: "0 !important",
                                        },
                                        "& [data-cy='header-register']": { width: 280 },
                                        "& [data-cy='header-regime'], & [data-cy='register-regime-cell-content']": {
                                            width: 260,
                                            overflow: "hidden",
                                        },
                                        "& [data-cy='header-assets']": { width: 90 },
                                        "& [data-cy='header-status']": { width: statusColumnWidth },
                                        "& [data-cy='register-status-cell-content']": { minWidth: statusColumnWidth },
                                        "& [data-cy='header-lastModified']": { width: 160 },
                                        "& [data-cy='header-actions']": { width: 64 },
                                        "& [data-cy='register-list-item-row'] > .MuiTableCell-root:first-of-type, & [data-cy='register-list-item-row'] > .MuiTableCell-root:last-of-type":
                                            {
                                                position: "sticky",
                                                bgcolor: "background.default",
                                                zIndex: 2,
                                            },
                                        "& [data-cy='register-list-item-row'] > .MuiTableCell-root:first-of-type": {
                                            left: 0,
                                        },
                                        "& [data-cy='register-list-item-row'] > .MuiTableCell-root:last-of-type": {
                                            right: 0,
                                        },
                                        "& [data-cy='register-list-header-row'] > .MuiTableCell-root:first-of-type, & [data-cy='register-list-header-row'] > .MuiTableCell-root:last-of-type":
                                            {
                                                position: "sticky",
                                                bgcolor: "background.neutral",
                                                zIndex: 203,
                                            },
                                        "& [data-cy='register-list-header-row'] > .MuiTableCell-root:first-of-type": {
                                            left: 0,
                                        },
                                        "& [data-cy='register-list-header-row'] > .MuiTableCell-root:last-of-type": {
                                            right: 0,
                                        },
                                        "& [data-cy='register-list-item-row']:hover > .MuiTableCell-root:first-of-type, & [data-cy='register-list-item-row']:hover > .MuiTableCell-root:last-of-type, & [data-cy='register-list-item-row']:focus-visible > .MuiTableCell-root:first-of-type, & [data-cy='register-list-item-row']:focus-visible > .MuiTableCell-root:last-of-type":
                                            {
                                                backgroundImage: `linear-gradient(${hoverBg}, ${hoverBg})`,
                                                backgroundColor: "background.default",
                                            },
                                    }}
                                >
                                    <Table
                                        size="small"
                                        sx={{
                                            tableLayout: "fixed",
                                            "& .MuiTableRow-root": {
                                                "&": {
                                                    borderBottom: "1px solid " + theme.palette.action.hover,
                                                },
                                                "&:last-of-type": {
                                                    borderBottom: "none",
                                                },
                                            },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    position: "sticky",
                                                    top: 0,
                                                    bgcolor: "background.paper",
                                                    zIndex: 202,
                                                    "& th:first-of-type": {
                                                        borderTopLeftRadius: 8,
                                                        borderBottomLeftRadius: 8,
                                                    },
                                                    "& th:last-of-type": {
                                                        borderTopRightRadius: 8,
                                                        borderBottomRightRadius: 8,
                                                    },
                                                }}
                                                data-cy="register-list-header-row"
                                            >
                                                <Repeat
                                                    keyFn={pick("field")}
                                                    list={registerHeaders}
                                                    item={<FieldHeader />}
                                                />
                                                <TableCell data-cy="header-actions" />
                                            </TableRow>
                                        </TableHead>
                                        <RequestPageRepeat
                                            bind
                                            pageSize={take}
                                            refreshKey={`${search ?? ""}|${rawSort}`}
                                            total={totalCount}
                                            Component={TableBody}
                                            list={registers}
                                            setSkip={setSkip}
                                            item={<Register />}
                                            hidePaginator={true}
                                        />
                                    </Table>
                                </FitInRemainingSpace>
                            </Else>
                        </If>
                        <Box sx={{ flexShrink: 0, pb: 2 }}>
                            <RegisterPaginationBottom.Slot
                                take={take}
                                setTake={setTake}
                                skip={skip}
                                setSkip={setSkip}
                                totalCount={totalCount}
                            />
                        </Box>
                    </Stack>
                </Else>
            </If>
        </Bound>
    )

    function resetPageOnSortChange() {
        setSkip(0)
    }
}
